import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { Loader2, Send, Sparkles, Trash2, X } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'
import { useShowAiProvider } from '../hooks/useShowAiProvider'
import { cn } from '../lib/utils'

// Phoenix Assistant — multi-turn chat bottom sheet.
//
// Messages live in component state for the session: { role, content }.
// On each turn we send the whole message array to the edge function;
// the edge function fans that out to the LLM with the user's data
// context prepended once.
//
// History is NOT persisted to the DB this round. Closing the sheet
// keeps the history in memory until the user clears it manually
// (trash icon) or refreshes the page.

const ENTER_MS = 260
const EXIT_MS = 220

// Suggested prompts shown when the chat is empty. Tapping one writes
// it into the input box and submits it immediately — same behaviour
// as typing it and tapping send.
const SUGGESTIONS_EN = [
  'How did my weight trend this month?',
  'What\'s my average daily kcal lately?',
  'Did I work out enough last week?',
  'What\'s a realistic weight goal for next month?',
]
const SUGGESTIONS_AR = [
  'كم تغير وزنى الشهر ده؟',
  'كم متوسط السعرات اليومى مؤخرا؟',
  'هل لعبت تمارين كفايه الأسبوع اللى فات؟',
  'ايه هدف وزن واقعى للشهر الجاى؟',
]

export default function AssistantSheet({ open, onClose }) {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'

  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (open) {
      setMounted(true)
      const r = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(r)
    }
    setVisible(false)
    const t = setTimeout(() => setMounted(false), EXIT_MS)
    return () => clearTimeout(t)
  }, [open])

  // messages[]: [{ role: 'user' | 'assistant', content }]. Kept in
  // component state for the session so the user sees the
  // conversation as it grows. Survives closing/reopening the sheet
  // because the parent (AssistantHost in App.jsx) doesn't unmount
  // this component on close.
  const [messages, setMessages] = useState([])
  const [pending, setPending] = useState(false)
  const [question, setQuestion] = useState('')
  const [error, setError] = useState(null)
  const inputRef = useRef(null)
  const scrollRef = useRef(null)

  // Auto-scroll to the bottom of the chat when a new message arrives.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, pending])

  // Focus the input on open so the keyboard summons immediately.
  useEffect(() => {
    if (!visible || !inputRef.current) return
    const r = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(r)
  }, [visible])

  const handleAsk = useCallback(async (q) => {
    const text = (q ?? question).trim()
    if (!text || pending) return
    setError(null)
    setQuestion('')
    // Append user message first so the UI feels responsive.
    const nextMessages = [...messages, { role: 'user', content: text }]
    setMessages(nextMessages)
    setPending(true)
    try {
      const { data, error: err } = await supabase.functions.invoke('phoenix-assistant', {
        body: { messages: nextMessages, language: isAr ? 'ar' : 'en' },
      })
      if (err) throw err
      if (data?.error) {
        setError(data.error)
      } else if (data?.answer) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.answer, provider: data.provider }])
      } else {
        setError('Empty response')
      }
    } catch (e) {
      setError(e?.message || 'failed')
    } finally {
      setPending(false)
    }
  }, [question, pending, messages, isAr])

  const handleSubmit = (e) => {
    e.preventDefault()
    handleAsk()
  }

  const handleSuggestion = (s) => {
    handleAsk(s)
  }

  const handleClear = () => {
    setMessages([])
    setError(null)
  }

  if (!mounted) return null
  if (typeof document === 'undefined') return null

  const suggestions = isAr ? SUGGESTIONS_AR : SUGGESTIONS_EN
  const hasMessages = messages.length > 0

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        style={{ transitionDuration: `${visible ? ENTER_MS : EXIT_MS}ms` }}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Sheet body */}
      <div
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 bg-card border-t border-border rounded-t-2xl shadow-2xl',
          'flex flex-col max-h-[85vh]',
          'transition-transform',
          visible ? 'translate-y-0' : 'translate-y-full',
        )}
        style={{
          transitionDuration: `${visible ? ENTER_MS : EXIT_MS}ms`,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assistant-sheet-title"
      >
        {/* Grabber */}
        <div className="flex justify-center pt-2 pb-1" aria-hidden="true">
          <span className="w-10 h-1 rounded-full bg-foreground/20" />
        </div>

        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border/60">
          <Sparkles className="w-5 h-5 text-app-primary-500" />
          <div className="flex-1 min-w-0">
            <h2 id="assistant-sheet-title" className="text-base font-bold text-foreground" dir="auto">
              {t('assistant.title')}
            </h2>
            <p className="text-[11px] text-muted-foreground" dir="auto">
              {t('assistant.subtitle')}
            </p>
          </div>
          {hasMessages && (
            <button
              type="button"
              onClick={handleClear}
              aria-label={t('assistant.clearChat')}
              title={t('assistant.clearChat')}
              className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Message list. When empty, render the suggestion chips
            instead of an awkward blank space. */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
          {!hasMessages && !pending && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground" dir="auto">
                {t('assistant.suggestionsHint')}
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSuggestion(s)}
                    className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-accent text-foreground transition-colors"
                    dir="auto"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, idx) => (
            <MessageBubble key={idx} message={m} isAr={isAr} />
          ))}

          {pending && (
            <div className="inline-flex items-center gap-2 text-xs text-muted-foreground py-2 px-3 rounded-2xl bg-muted/60" dir="auto">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>{t('assistant.thinking')}</span>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 px-3 py-2 rounded-md bg-red-500/10" dir="auto">
              {t('assistant.error', { error })}
            </div>
          )}
        </div>

        {/* Composer */}
        <form
          onSubmit={handleSubmit}
          className="flex items-end gap-2 px-3 py-3 border-t border-border/60"
        >
          <textarea
            ref={inputRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                handleAsk()
              }
            }}
            rows={2}
            placeholder={t('assistant.placeholder')}
            disabled={pending}
            // No dir override — inherits the document direction so
            // the placeholder anchors to the correct edge (right in
            // RTL, left in LTR). defaulted empty inputs
            // to LTR which mis-anchored the Arabic placeholder.
            className="flex-1 min-h-[44px] max-h-32 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground resize-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={pending || !question.trim()}
            aria-label={t('assistant.send')}
            className={cn(
              'h-11 w-11 rounded-lg inline-flex items-center justify-center transition-colors flex-shrink-0',
              question.trim() && !pending
                ? 'bg-app-primary-500 text-white hover:bg-app-primary-500/90'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {pending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5 rtl:-scale-x-100" />
            )}
          </button>
        </form>
      </div>
    </>,
    document.body,
  )
}

// One message in the chat. User on the right (start side in LTR, end
// in RTL) with the primary tint; assistant on the left, plain card.
// Markdown renders for the assistant; user messages are plain text.
function MessageBubble({ message, isAr }) {
  const isUser = message.role === 'user'
  const { enabled: showProvider } = useShowAiProvider()
  return (
    <div
      className={cn(
        'flex',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
          isUser
            ? 'bg-app-primary-500 text-white rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm',
        )}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}
        {message.provider && !isUser && showProvider && (
          <p className="text-[9px] text-muted-foreground/70 font-mono mt-1" dir="ltr">
            via {message.provider}
          </p>
        )}
      </div>
    </div>
  )
}
